import axios, { AxiosHeaders } from 'axios';

import API from './Api';
import APIExport from './ApiExport';
import MgrApi from './MgrApi';

const WORKSPACE_SCOPE_HEADERS = ['X-Workspace-Id', 'X-Workspace-View-Id'] as const;

// Every client authenticated by the session cookie. The backend takes the workspace role and the tasks from these
// headers; without them it falls back to the user's default workspace, which may belong to another client.
const scopedClients = [API, APIExport, MgrApi];

export const applyWorkspaceScopeHeaders = (headers: Record<string, string | null>) => {
  scopedClients.forEach((client) => Object.assign(client.defaults.headers.common, headers));
};

/**
 * Drops the workspace scope once the session leaves its client, on a client switch or a logout. A request that still
 * carries a workspace of the previous client is rejected with 403 when the backend enforces the authorized context.
 */
export const clearWorkspaceScopeHeaders = () => {
  scopedClients.forEach((client) => WORKSPACE_SCOPE_HEADERS.forEach((header) => {
    delete client.defaults.headers.common[header];
  }));
};

const scopeValue = (value: unknown) => String(value ?? '');

/**
 * Tells whether a failed request carried a workspace scope the session has since left, like a request still in flight
 * while the user switched workspace or client. Its 403 judges that old scope, not the screen the user is on now.
 */
export const wasSentWithPreviousWorkspaceScope = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;

  const sentHeaders = AxiosHeaders.from(error.config?.headers ?? {});
  if (!scopeValue(sentHeaders.get('X-Workspace-Id'))) return false;

  const currentHeaders = API.defaults.headers.common;
  return WORKSPACE_SCOPE_HEADERS.some((header) => (
    scopeValue(sentHeaders.get(header)) !== scopeValue(currentHeaders[header])
  ));
};
